import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../services/firebase';
import { collection, query, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore';

/**
 * Hook personnalisé pour synchroniser les données avec Firebase en temps réel
 * 
 * @param {string} collectionName - Nom de la collection Firestore
 * @param {Object} options - Options de configuration
 * @param {string} options.orderByField - Champ pour le tri (default: 'createdAt')
 * @param {string} options.orderDirection - Direction du tri ('asc' ou 'desc')
 * @param {number} options.limit - Nombre maximum de documents à charger
 * @param {boolean} options.autoSync - Activer la synchronisation automatique périodique (pour la collection des commandes)
 * @param {Array} options.where - Conditions de filtre pour la requête
 * @returns {Object} - { data, loading, error, syncWithShopify, lastSyncTime }
 */
const useFirebaseSync = (collectionName, options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Utiliser useRef pour éviter les problèmes de fermeture (closure) et de rendus en boucle
  const isSyncingRef = useRef(false);
  const lastSyncRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const dataRef = useRef([]);
  const unsubscribeRef = useRef(null);
  const mountedRef = useRef(true);
  const optionsRef = useRef(options);
  
  // Mettre à jour optionsRef uniquement si les options ont réellement changé
  useEffect(() => {
    if (JSON.stringify(options) !== JSON.stringify(optionsRef.current)) {
      optionsRef.current = options;
    }
  }, [options]);

  // Fonction pour synchroniser avec Shopify
  const syncWithShopify = useCallback(async (force = false) => {
    // Éviter les synchronisations simultanées
    if (isSyncingRef.current && !force) {
      console.log('Synchronisation déjà en cours, ignorée');
      return { success: false, skipped: true };
    }
    
    // Limiter la fréquence des synchronisations (pas plus d'une fois toutes les 30 secondes)
    const now = new Date();
    if (lastSyncRef.current && !force) {
      const timeSinceLastSync = now - lastSyncRef.current;
      if (timeSinceLastSync < 30000) { // 30 secondes
        console.log(`Synchronisation trop fréquente (${Math.round(timeSinceLastSync/1000)}s), ignorée`);
        return { success: false, skipped: true, tooFrequent: true };
      }
    }
    
    try {
      isSyncingRef.current = true;
      
      // Vérifier si le token est disponible
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('Synchronisation: Pas de token disponible');
        if (mountedRef.current) {
          setError(new Error('Pas de token disponible'));
        }
        return { success: false, error: 'Pas de token disponible' };
      }
      
      const apiUrl = process.env.REACT_APP_API_URL;
      
      console.log('Synchronisation des nouvelles commandes...');
      const response = await fetch(`${apiUrl}/api/orders/sync-new`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erreur HTTP (${response.status}): ${errorText}`);
        throw new Error(`Erreur lors de la synchronisation (${response.status}): ${errorText}`);
      }
      
      let responseData;
      try {
        responseData = await response.json();
        console.log('Résultat de la synchronisation:', responseData);
      } catch (jsonError) {
        console.error('Erreur lors du parsing JSON:', jsonError);
        throw new Error('Réponse invalide du serveur');
      }
      
      // Mettre à jour l'heure de la dernière synchronisation
      const syncTime = new Date();
      if (mountedRef.current) {
        setLastSyncTime(syncTime);
      }
      lastSyncRef.current = syncTime;
      
      // Seulement si de nouvelles commandes ont été trouvées, forcer un rechargement
      if (responseData.orders && responseData.orders.length > 0) {
        console.log(`${responseData.orders.length} nouvelles commandes trouvées, rechargement des données...`);
        // Rafraîchir manuellement les données au lieu de déclencher un re-rendu
        fetchDataFromFirestore();
      } else {
        console.log('Aucune nouvelle commande trouvée');
      }
      
      return { 
        success: true, 
        ordersCount: responseData.orders?.length || 0,
        message: responseData.message
      };
    } catch (error) {
      console.error('Erreur de synchronisation:', error);
      if (mountedRef.current) {
        setError(error);
      }
      return { success: false, error: error.message || 'Erreur inconnue' };
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  // Fonction pour récupérer les données de Firestore
  const fetchDataFromFirestore = useCallback(async () => {
    if (!mountedRef.current) return;
    
    try {
      setLoading(true);
      
      // Construire la requête
      let queryRef = collection(db, collectionName);
      let queryConstraints = [];

      // Ajouter le tri par date de création
      if (optionsRef.current.orderByField) {
        queryConstraints.push(orderBy(optionsRef.current.orderByField, optionsRef.current.orderDirection || 'desc'));
      }

      // Ajouter la limite si spécifiée
      if (optionsRef.current.limit) {
        queryConstraints.push(limit(optionsRef.current.limit));
      }

      // Créer la requête de base avec tri et limite
      const q = query(queryRef, ...queryConstraints);
      
      // Exécuter la requête manuellement pour éviter les problèmes avec onSnapshot
      const snapshot = await getDocs(q);
      
      // Récupérer les données
      let fetchedData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Appliquer les filtres côté client pour éviter les problèmes d'index
      if (optionsRef.current.where && Array.isArray(optionsRef.current.where)) {
        optionsRef.current.where.forEach(condition => {
          if (condition.field && condition.operator && condition.value !== undefined) {
            fetchedData = fetchedData.filter(item => {
              const fieldValue = item[condition.field];
              
              switch (condition.operator) {
                case '==':
                  return fieldValue === condition.value;
                case '!=':
                  return fieldValue !== condition.value;
                case '>':
                  return fieldValue > condition.value;
                case '>=':
                  return fieldValue >= condition.value;
                case '<':
                  return fieldValue < condition.value;
                case '<=':
                  return fieldValue <= condition.value;
                default:
                  return true;
              }
            });
          }
        });
      }
      
      // Vérifier si les données ont changé avant de mettre à jour l'état
      if (JSON.stringify(fetchedData) !== JSON.stringify(dataRef.current)) {
        dataRef.current = fetchedData;
        if (mountedRef.current) {
          setData(fetchedData);
        }
      }
      
      if (mountedRef.current) {
        setLoading(false);
        setError(null);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des données:', err);
      if (mountedRef.current) {
        setError(err);
        setLoading(false);
      }
    }
  }, [collectionName]);

  // Configurer l'abonnement aux données Firestore
  useEffect(() => {
    mountedRef.current = true;
    
    // Fonction pour configurer l'abonnement
    const setupSubscription = async () => {
      // Nettoyer l'abonnement précédent si existant
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      try {
        // Charger les données initiales
        await fetchDataFromFirestore();
        
        // Si c'est la collection des commandes, configurer une synchronisation périodique
        if (collectionName === 'orders' && optionsRef.current.autoSync) {
          // Synchroniser au démarrage après un court délai (seulement pour les commandes)
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }
          
          syncTimeoutRef.current = setTimeout(() => {
            // Vérifier si une synchronisation n'est pas déjà en cours
            if (!isSyncingRef.current && mountedRef.current) {
              syncWithShopify().catch(err => {
                console.error('Erreur lors de la synchronisation initiale:', err);
              });
            }
          }, 10000); // Attendre 10 secondes avant la première synchronisation
          
          // Configurer la synchronisation périodique (toutes les 10 minutes)
          if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
          }
          
          syncIntervalRef.current = setInterval(() => {
            // Vérifier si une synchronisation n'est pas déjà en cours
            if (!isSyncingRef.current && mountedRef.current) {
              syncWithShopify().catch(err => {
                console.error('Erreur lors de la synchronisation périodique:', err);
              });
            }
          }, 10 * 60 * 1000); // 10 minutes
        }
      } catch (err) {
        console.error('Erreur lors de l\'initialisation:', err);
        if (mountedRef.current) {
          setError(err);
          setLoading(false);
        }
      }
    };
    
    setupSubscription();
    
    // Nettoyer les abonnements lors du démontage
    return () => {
      mountedRef.current = false;
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
      
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, [collectionName, fetchDataFromFirestore, syncWithShopify]);

  // Mettre en place un rafraîchissement périodique des données pour la collection des commandes
  useEffect(() => {
    if (collectionName === 'orders') {
      const refreshInterval = setInterval(() => {
        if (mountedRef.current && !isSyncingRef.current) {
          fetchDataFromFirestore();
        }
      }, 60000); // Rafraîchir toutes les minutes
      
      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [collectionName, fetchDataFromFirestore]);

  return { data, loading, error, syncWithShopify, lastSyncTime };
};

export default useFirebaseSync;
